import { Box, Flex } from '@chakra-ui/react';
import { AttachmentChip } from './AttachmentChip';
import { SCORECARD_GENERATING_ROW_LABEL } from './attachFilesCopy';
import type { SelectedAttachment } from './useOfferAttachments';

interface SelectedAttachmentChipsProps {
  attachments: SelectedAttachment[];
  // Deselect ONLY — never a delete/purge call (contract seam_2 MUST_NOT, case X1).
  onRemove: (attachment: SelectedAttachment) => void;
  /**
   * The file currently uploading, if any. A placeholder chip carrying its name
   * and size is appended, so the row shows the document the moment it is
   * picked rather than staying empty until the server answers.
   */
  uploadingFile?: { filename: string; byte_size: number } | null;
  /**
   * `attachmentKey` of the chip being removed. The ✕ PUTs the remaining
   * selection and the chip only goes once the server confirms, so it holds a
   * loader in the meantime. Keyed rather than a boolean so the other chips are
   * left alone.
   */
  removingKey?: string | null;
}

// Stands in for a file mid-upload: named and sized from the File the recruiter
// picked, so the chip says which document is on its way rather than just that
// something is. It carries no url, so the chip offers no preview until the real
// record replaces it.
const pendingPlaceholder = (file: {
  filename: string;
  byte_size: number;
}): SelectedAttachment => ({
  ref_type: 'offer_document',
  ref_id: null,
  filename: file.filename,
  content_type: '',
  byte_size: file.byte_size,
  url: '',
});

// Design.md `2498:517` — chips under the note body: filename, size, preview, ✕.
// Compact and wrapping rather than one full-width row per file: the note card
// is narrow and a stack of full-width rows pushed the approval flow below the
// fold as soon as two documents were attached.
export const SelectedAttachmentChips = ({
  attachments,
  onRemove,
  uploadingFile = null,
  removingKey = null,
}: SelectedAttachmentChipsProps) => {
  if (!attachments.length && !uploadingFile) return null;

  return (
    // Two per row, each taking half the width — a grid rather than wrapping
    // flex, so both columns stay equal however long the filenames are instead
    // of each chip sizing to its own content. The names truncate, which is what
    // keeps this workable at drawer widths.
    <Box
      display="grid"
      gridTemplateColumns="repeat(2, minmax(0, 1fr))"
      gap="8px"
      p="12px 16px"
      borderTop="1px solid"
      borderColor="border-color"
      data-test-id="selected-attachment-chips"
    >
      {attachments.map((attachment) =>
        // The PDF starts rendering when Add is clicked, so this is the chip the
        // recruiter is looking at when it happens. Preview and ✕ would both act on
        // a blob that doesn't exist yet, so the loader takes their place.
        attachment.status === 'generating' ? (
          // Same chip, in its loading state — a bare spinner row beside proper
          // chips read as a different kind of thing entirely.
          <Flex
            key={`${attachment.ref_type}:${attachment.ref_id ?? ''}`}
            data-test-id="attachment-chip-generating"
          >
            <AttachmentChip
              attachment={{
                ...attachment,
                filename: `${attachment.filename} — ${SCORECARD_GENERATING_ROW_LABEL}`,
              }}
              onRemove={onRemove}
              loading
            />
          </Flex>
        ) : (
          <AttachmentChip
            key={`${attachment.ref_type}:${attachment.ref_id ?? ''}`}
            attachment={attachment}
            onRemove={onRemove}
            loading={
              removingKey ===
              `${attachment.ref_type}:${attachment.ref_id ?? ''}`
            }
          />
        )
      )}
      {uploadingFile && (
        <AttachmentChip
          attachment={pendingPlaceholder(uploadingFile)}
          onRemove={() => {}}
          loading
        />
      )}
    </Box>
  );
};
